/**
 * GuidancePanel — v3
 * ──────────────────
 * Four tabs:
 *   📋 Guided Flows   — send pre-built server flows
 *   ✨ Highlight      — build and send highlight sequences
 *   🔴 Record         — record a live demo into a reusable sequence
 *   🎨 Designer       — build sequences step-by-step manually
 *
 * WHAT'S NEW — Record tab
 * ───────────────────────
 * The agent clicks "Start Recording", then works normally:
 *   • Highlights elements in the Highlight tab → each becomes a step
 *   • Types chat messages → each message becomes the instruction text
 *     for the most recent highlighted step
 *
 * When done, they click "Stop Recording", give the sequence a name,
 * and save it. It appears immediately in the Sequence Library.
 *
 * The `recorder` prop is a FlowRecorder from useFlowRecorder().
 * App.tsx wires up recordHighlight() and recordText() into the
 * existing sendHighlight and sendAgentChat handlers.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { UserInfo, UiElement, Toast } from '../../types';
import { FLOWS, LANG_NAMES } from '../../constants';
import type { FlowRecorder, RecordedStep } from '../../hooks/useFlowRecorder';
import type { SequenceSession } from './SequenceLibrary';
import { UiTreeViewer } from '../uitree/UiTreeViewer';
import s from './GuidancePanel.module.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  user:                    UserInfo | null;
  uiElements:              UiElement[];
  onSendFlow:              (userId: string, flowId: string) => void;
  onSendHighlight:         (userId: string, seq: string[], ms: number) => void;
  /**
   * Required when the Highlight tab is used: agent picks ONE node in the
   * tree → server walks the persisted UiTreeRegistry upward → builds the
   * full step sequence from the topmost landing point (side menu / top bar)
   * down to the target → sends it as a multi-step guided flow.
   *
   * App.tsx wires this to the SignalR sendSequenceToElement call.
   */
  onSendSequence:          (targetElementId: string, label: string) => void;
  addToast:                (t: Omit<Toast, 'id'>) => void;
  pendingElementId?:       string | null;
  onPendingElementConsumed?: () => void;
  /** FlowRecorder from useFlowRecorder() — wired in App.tsx */
  recorder:                FlowRecorder;
  /** Called when the agent finishes a recording and wants to save it */
  onSaveRecording:         (seq: SequenceSession) => void;
}

type Tab = 'flows' | 'highlight' | 'record' | 'designer';

const uid = () => Math.random().toString(36).slice(2, 10);

// ── Group elements by section for the element picker ─────────────────────────

// NOTE: the old `groupElements()` function used to live here and grouped
// elements by hardcoded LP_* id prefixes (LP_NAV_, LP_SHIP_, LP_JOB_, …).
// That made sense when the registry was hand-authored with stable
// constant names. With auto-discovery using v4 GUIDs, every discovered
// element fell into "Other" while only legacy hand-authored items
// showed under "Shipment" / "Job" / etc — confusing duplicate views.
//
// The Highlight tab now uses <UiTreeViewer> directly, which builds a
// real parent-child tree from the data the server has. One canonical
// source. No more hardcoded buckets.

// ── Sub-component: step list used in Record and Designer ─────────────────────

const StepList: React.FC<{
  steps: { elementId: string | null; label: string; textEn: string; highlightStyle: string }[];
  onUndo?: () => void;
}> = ({ steps, onUndo }) => {
  if (steps.length === 0) return (
    <div className={s.emptyMini}>No steps recorded yet</div>
  );

  return (
    <div className={s.stepList}>
      {steps.map((step, i) => {
        const red = step.highlightStyle === 'redPulse';
        return (
          <div key={i} className={`${s.stepRow} ${step.textEn ? '' : s.stepRowPending}`}>
            <span className={`${s.stepNum} ${red ? s.stepNumRed : ''}`}>{i + 1}</span>
            <div className={s.stepBody}>
              {step.elementId && <div className={s.stepId}>{step.elementId}</div>}
              <div className={`${s.stepText} ${step.textEn ? '' : s.stepTextPending}`}>
                {step.textEn || '(waiting for instruction text…)'}
              </div>
            </div>
            <span className={`${s.stepTag} ${red ? s.stepTagRed : ''}`}>{red ? '🔴' : '✨'}</span>
          </div>
        );
      })}
      {onUndo && steps.length > 0 && (
        <button onClick={onUndo} className={s.undoBtn}>↩ Undo last step</button>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export const GuidancePanel: React.FC<Props> = ({
  user, uiElements, onSendFlow, onSendHighlight, onSendSequence, addToast,
  pendingElementId, onPendingElementConsumed,
  recorder, onSaveRecording,
}) => {
  const [tab,    setTab]    = useState<Tab>('flows');
  const [flowId, setFlowId] = useState<string | null>(null);

  // Highlight tab state
  // ── Highlight tab state ──────────────────────────────────────────────────
  // Single-pick model: clicking a tree node sends immediately. No local
  // `seq` array, no style toggle, no delay select. The server's
  // TreeSequenceBuilder computes the steps; the customer-side advancer
  // applies the redPulse style by default.
  // (search state removed — the UiTreeViewer has its own search input)

  // Record tab state — save dialog
  const [showSave,   setShowSave]   = useState(false);
  const [recTitle,   setRecTitle]   = useState('');
  const [recTags,    setRecTags]    = useState('');
  const [recDesc,    setRecDesc]    = useState('');
  const [editStepIdx,setEditStepIdx]= useState<number | null>(null);
  const [editText,   setEditText]   = useState('');

  // Designer tab state
  const [dPickEl,  setDPickEl]   = useState('');
  const [dText,    setDText]     = useState('');
  const [dStyle,   setDStyle]    = useState<'redPulse' | 'gold'>('redPulse');
  const [dWait,    setDWait]     = useState(true);
  const [dInterval,setDInterval] = useState(2000);
  const [dSteps,   setDSteps]    = useState<RecordedStep[]>([]);
  const [dTitle,   setDTitle]    = useState('');
  const [dTags,    setDTags]     = useState('');

  // When element selected from UI Tree → switch to highlight tab so the
  // agent can pick from the same tree there. We no longer pre-seed `seq`
  // because the Highlight tab is now single-pick (one click → server
  // walks the tree). The agent re-clicks the desired target in the
  // Highlight tab if they want to actually send.
  useEffect(() => {
    if (pendingElementId) {
      setTab('highlight');
      onPendingElementConsumed?.();
    }
  }, [pendingElementId, onPendingElementConsumed]);

  // Flat sorted list of all elements — used by the Designer tab's element
  // <select>. Replaces the old `grouped` useMemo that bucketed by hardcoded
  // LP_* prefixes. Sorted by route then label so the select is browsable.
  const elementsByRoute = useMemo(() => {
    const groups: Record<string, UiElement[]> = {};
    for (const el of uiElements) {
      const route = el.route ?? '(no route)';
      (groups[route] ||= []).push(el);
    }
    for (const route in groups) {
      groups[route].sort((a, b) => a.label.localeCompare(b.label));
    }
    return groups;
  }, [uiElements]);

  // (Old `toggleEl` and `doSendHighlight` helpers removed — the Highlight
  //  tab is now single-pick and sends immediately via onSendSequence.
  //  The Designer tab still calls onSendHighlight directly when replaying
  //  its own step list.)

  // ── Record tab actions ────────────────────────────────────────────────────

  const handleStartRecording = () => {
    recorder.start();
    setTab('highlight'); // jump to highlight so agent can start highlighting
    addToast({ title: '🔴 Recording started', msg: 'Highlight elements, then type your instructions in chat', color: 'red' });
  };

  const handleStopRecording = () => {
    const steps = recorder.stop();
    if (steps.length === 0) {
      addToast({ title: 'No steps recorded', msg: 'Highlight elements and type messages first', color: 'amber' });
      return;
    }
    setShowSave(true);
    setTab('record');
  };

  const handleSaveRecording = () => {
    const steps = recorder.state.steps;
    if (!recTitle.trim() || steps.length === 0) return;

    const seq: SequenceSession = {
      id:          'SEQ-' + uid().toUpperCase(),
      title:       recTitle.trim(),
      description: recDesc.trim(),
      tags:        recTags.split(',').map(t => t.trim()).filter(Boolean),
      steps:       steps.map(st => ({
        elementId:      st.elementId ?? '',
        label:          st.label,
        textEn:         st.textEn || st.label,
        route:          st.route,
        highlightStyle: st.highlightStyle,
        waitForClick:   st.waitForClick,
        tooltipEn:      undefined,
        intervalMs:     st.intervalMs,
      })),
      createdBy:   'agent',
      createdAt:   new Date().toISOString(),
      playCount:   0,
    };

    onSaveRecording(seq);
    setShowSave(false);
    setRecTitle(''); setRecDesc(''); setRecTags('');
    recorder.discard();
    addToast({ title: '✅ Sequence saved', msg: `"${seq.title}" added to library`, color: 'green' });
  };

  // ── Edit step text inline (in save dialog) ────────────────────────────────

  const saveStepEdit = () => {
    if (editStepIdx === null) return;
    // Update via recorder internals — we call recordText-equivalent
    recorder.state.steps[editStepIdx] = { ...recorder.state.steps[editStepIdx], textEn: editText };
    setEditStepIdx(null);
    setEditText('');
  };

  // ── Designer tab ──────────────────────────────────────────────────────────

  const addDesignerStep = () => {
    if (!dPickEl) return;
    const el = uiElements.find(e => e.id === dPickEl);
    setDSteps(p => [...p, {
      elementId:      dPickEl,
      label:          el?.label ?? dPickEl,
      textEn:         dText || `Click ${el?.label ?? dPickEl}`,
      route:          el?.route,
      highlightStyle: dStyle,
      waitForClick:   dWait,
      intervalMs:     dInterval,
      recordedAt:     Date.now(),
    }]);
    setDPickEl(''); setDText('');
  };

  const saveDesignerFlow = () => {
    if (!dTitle.trim() || dSteps.length === 0) {
      addToast({ title: 'Add a title and at least one step', color: 'amber' });
      return;
    }
    const seq: SequenceSession = {
      id:          'SEQ-' + uid().toUpperCase(),
      title:       dTitle.trim(),
      description: '',
      tags:        dTags.split(',').map(t => t.trim()).filter(Boolean),
      steps:       dSteps.map(st => ({
        elementId:      st.elementId ?? '',
        label:          st.label,
        textEn:         st.textEn,
        route:          st.route,
        highlightStyle: st.highlightStyle,
        waitForClick:   st.waitForClick,
        tooltipEn:      undefined,
        intervalMs:     st.intervalMs,
      })),
      createdBy:   'agent',
      createdAt:   new Date().toISOString(),
      playCount:   0,
    };
    onSaveRecording(seq);
    setDTitle(''); setDTags(''); setDSteps([]);
    addToast({ title: '✅ Sequence saved', msg: `"${seq.title}" added to library`, color: 'green' });
  };

  const playDesignerNow = () => {
    if (!user || dSteps.length === 0) return;
    const ids = dSteps.filter(s => s.elementId).map(s => s.elementId!);
    if (ids.length) onSendHighlight(user.id, ids, dSteps[0]?.intervalMs ?? 2000);
    addToast({ title: '▶ Playing designer flow', msg: `${dSteps.length} steps → ${user.name}`, color: 'green' });
  };

  // ── No user selected ──────────────────────────────────────────────────────

  if (!user && !recorder.state.isRecording) return (
    <div className={s.noUser}>
      <div className={s.noUserIco}>🧭</div>
      <div className={s.noUserTitle}>No User Selected</div>
      <div className={s.noUserSub}>
        Select a user from the left panel to send guided flows, record a live demo, or highlight elements in their browser.
      </div>
    </div>
  );

  // ── Recording indicator (visible in all tabs while recording) ────────────

  const RecordingBar = () => recorder.state.isRecording ? (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px',
      background: '#FFEBEE', borderBottom: '1px solid #FFCDD2', flexShrink: 0,
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#E53935', animation: 'pulse 1s infinite', flexShrink: 0 }}/>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#C62828', flex: 1 }}>
        Recording — {recorder.state.steps.length} step{recorder.state.steps.length !== 1 ? 's' : ''} captured
      </span>
      <button onClick={handleStopRecording} style={{
        background: '#E53935', color: '#fff', border: 'none', borderRadius: 'var(--r)',
        padding: '3px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer',
      }}>⏹ Stop</button>
      <button onClick={recorder.discard} style={{
        background: 'none', border: '1px solid #FFCDD2', borderRadius: 'var(--r)',
        padding: '3px 8px', fontSize: 10, color: '#C62828', cursor: 'pointer',
      }}>Discard</button>
    </div>
  ) : null;

  // Currently selected flow (Flows tab) — drives the pinned send bar below.
  const selectedFlow = flowId ? FLOWS.find(f => f.id === flowId) ?? null : null;

  return (
    <div className={s.panel}>
      {/* Recording status bar */}
      <RecordingBar />

      {/* Tabs */}
      <div className={s.subTabs}>
        <button className={`${s.stab} ${tab === 'flows'     ? s.stabActive : ''}`} onClick={() => setTab('flows')}>📋 Flows</button>
        <button className={`${s.stab} ${tab === 'highlight' ? s.stabActive : ''}`} onClick={() => setTab('highlight')}>✨ Highlight</button>
        <button className={`${s.stab} ${tab === 'record'    ? s.stabActive : ''}`} onClick={() => setTab('record')}
          style={{ color: recorder.state.isRecording ? '#E53935' : undefined }}>
          {recorder.state.isRecording ? '🔴' : '⚫'} Record
        </button>
        <button className={`${s.stab} ${tab === 'designer'  ? s.stabActive : ''}`} onClick={() => setTab('designer')}>🎨 Designer</button>
      </div>

      <div className={s.body}>

        {/* ══ FLOWS TAB ══════════════════════════════════════════════════════
            The send action lives in a pinned bar at the bottom of the panel
            (see `.flowFooter` below, rendered outside the scrolling body) so
            it stays visible no matter how far the flow grid is scrolled. */}
        {tab === 'flows' && <>
          <div className={s.flowsHead}>
            <div className={s.sLabel}>Guided Flows</div>
            <div className={s.flowsSub}>
              {user ? `Pick a flow to send to ${user.name}` : 'Select a user to send a flow'}
              {user?.lang && user.lang !== 'en' && (
                <span className={s.flowsLang}> · translates to {LANG_NAMES[user.lang] ?? user.lang}</span>
              )}
            </div>
          </div>
          <div className={s.flowGrid}>
            {FLOWS.map(f => (
              <div key={f.id}
                className={`${s.flowCard} ${flowId === f.id ? s.flowActive : ''}`}
                onClick={() => setFlowId(flowId === f.id ? null : f.id)}>
                {flowId === f.id && <span className={s.flowCheck}>✓</span>}
                <div className={s.flowIcon}>{f.icon}</div>
                <div className={s.flowTitle}>{f.title}</div>
                <div className={s.flowDesc}>{f.desc}</div>
                <div className={s.flowSteps}>{f.steps} steps</div>
              </div>
            ))}
          </div>
        </>}

        {/* ══ HIGHLIGHT TAB ══════════════════════════════════════════════════
            Single-selection model. The agent picks ONE node in the tree —
            the target the customer needs to ultimately click. The server
            walks the persisted UiTreeRegistry upward from that target to
            the topmost landing point (a side-menu or top-bar item) and
            builds the full sequence automatically. Customer sees the
            highlights play one at a time starting from the side menu and
            walking down to the picked target.

            We no longer maintain a local `seq` array because the agent
            isn't building the sequence by hand — the server does it from
            the target.
        ═════════════════════════════════════════════════════════════════ */}
        {tab === 'highlight' && <>
          {recorder.state.isRecording && (
            <div className={`${s.banner} ${s.bannerRec}`}>
              <span className={s.recDot}/> Recording active — highlights and chat messages are captured as steps
            </div>
          )}

          <div className={s.helpCard}>
            <span className={s.helpIco}>🎯</span>
            <div>
              Pick the single deepest element you want the user to reach. The server walks the
              tree from that target up to the side-menu or top-bar that contains it, then sends
              a guided sequence — the customer sees each step highlighted, with route changes
              handled automatically.
            </div>
          </div>

          {/* The tree picker — single click sends. The viewer's
              onSendSequence callback fires immediately when a node is
              clicked. No "build a list" step. */}
          {!user && (
            <div className={`${s.banner} ${s.bannerWarn}`}>
              ⚠️ Pick a user from the left panel first, then click a tree node.
            </div>
          )}

          <div className={s.treeBox}>
            <UiTreeViewer
              elements={uiElements}
              onSendSequence={(id, label) => {
                if (!user) {
                  addToast({
                    title: 'Select a user first',
                    msg:   'Pick a user from the left panel, then click a tree node.',
                    icon:  '⚠️',
                    color: 'amber',
                  });
                  return;
                }
                onSendSequence(id, label);
                addToast({
                  title: '🧭 Sequence sent',
                  msg:   `Server is building the walk to "${label}" for ${user.name}.`,
                  icon:  '🚀',
                  color: 'green',
                });
              }}
            />
          </div>
        </>}

        {/* ══ RECORD TAB ═════════════════════════════════════════════════════ */}
        {tab === 'record' && <>
          {!recorder.state.isRecording && !showSave && <>
            <div className={s.helpCard}>
              <span className={s.helpIco}>🔴</span>
              <div>Record a live demo session and save it as a reusable sequence. The system captures every element you highlight and every chat message you type.</div>
            </div>

            <div className={s.numSteps}>
              {[
                ['1', 'Click Start Recording'],
                ['2', 'Go to the Highlight tab and highlight elements for the user'],
                ['3', 'Type chat messages — they become the instruction for each highlighted step'],
                ['4', 'Come back here and click Stop Recording'],
                ['5', 'Name and save the sequence to the library'],
              ].map(([n, t]) => (
                <div key={n} className={s.numStep}>
                  <span className={s.numBadge}>{n}</span>
                  <span className={s.numText}>{t}</span>
                </div>
              ))}
            </div>

            <button onClick={handleStartRecording} className={s.recBtn}>
              🔴 Start Recording
            </button>
          </>}

          {/* While recording */}
          {recorder.state.isRecording && !showSave && <>
            <div className={`${s.banner} ${s.bannerRec}`}>
              <span className={s.recDot}/> Recording in progress — {recorder.state.steps.length} step{recorder.state.steps.length !== 1 ? 's' : ''} captured
            </div>
            <div className={s.helpText}>
              Go to the <strong>Highlight</strong> tab, pick elements and send them. Then type your instructions in the chat window. Come back here when done.
            </div>
            <div className={s.sLabel}>Captured steps</div>
            <StepList steps={recorder.state.steps} onUndo={recorder.undoLastStep} />
            <div className={s.actions}>
              <button className={s.btnGhost} onClick={recorder.discard}>Discard</button>
              <button className={s.btnAmber} onClick={handleStopRecording}>
                ⏹ Stop &amp; Save
              </button>
            </div>
          </>}

          {/* Save dialog */}
          {showSave && <>
            <div className={s.sLabel}>Save Recording</div>
            <div className={s.helpText}>
              {recorder.state.steps.length} steps captured. Give this sequence a name and save it to the library.
            </div>

            {/* Step text editor */}
            <div className={s.sLabel}>Review &amp; edit step text</div>
            <div className={s.stepList}>
              {recorder.state.steps.map((step, i) => (
                <div key={i} className={s.saveStep}>
                  <div className={s.saveStepHead}>
                    <span className={s.stepId} style={{ flex: 1 }}>
                      {step.elementId ?? 'text'}
                    </span>
                    <button className={s.miniBtn} onClick={() => { setEditStepIdx(i); setEditText(step.textEn); }}>
                      Edit
                    </button>
                  </div>
                  {editStepIdx === i ? (
                    <div style={{ display: 'flex', gap: 4, marginTop: 5 }}>
                      <input
                        className={s.input}
                        value={editText} onChange={e => setEditText(e.target.value)}
                        autoFocus
                        style={{ padding: '4px 7px', fontSize: 11 }}
                      />
                      <button className={`${s.miniBtn} ${s.miniBtnPrimary}`} onClick={saveStepEdit}>✓</button>
                      <button className={s.miniBtn} onClick={() => setEditStepIdx(null)}>✕</button>
                    </div>
                  ) : (
                    <div className={`${s.saveStepText} ${step.textEn ? '' : s.saveStepTextPending}`}>
                      {step.textEn || '(no text — will use element label)'}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Sequence metadata */}
            <div className={s.field}>
              <span className={s.fieldLabel}>Sequence title *</span>
              <input className={s.input}
                value={recTitle} onChange={e => setRecTitle(e.target.value)}
                placeholder="e.g. How to create a shipment"
              />
            </div>
            <div className={s.field}>
              <span className={s.fieldLabel}>Tags (comma separated)</span>
              <input className={s.input}
                value={recTags} onChange={e => setRecTags(e.target.value)}
                placeholder="e.g. shipment, create, new-user"
              />
            </div>

            <div className={s.actions}>
              <button className={s.btnGhost} onClick={() => { setShowSave(false); recorder.start(); }}>
                ← Re-record
              </button>
              <button className={s.btnGreen} disabled={!recTitle.trim()} onClick={handleSaveRecording}
                style={{ opacity: recTitle.trim() ? 1 : 0.5, cursor: recTitle.trim() ? 'pointer' : 'not-allowed' }}>
                💾 Save to Library
              </button>
            </div>
          </>}
        </>}

        {/* ══ DESIGNER TAB ═══════════════════════════════════════════════════ */}
        {tab === 'designer' && <>
          <div className={s.field}>
            <span className={s.fieldLabel}>Sequence title</span>
            <input className={s.input} value={dTitle} onChange={e => setDTitle(e.target.value)}
              placeholder="e.g. Shipment payment walkthrough"
            />
          </div>

          {/* Step builder */}
          <div className={s.builder}>
            <div className={s.sLabel}>Add step</div>
            <select className={s.select} value={dPickEl} onChange={e => setDPickEl(e.target.value)}>
              <option value="">— pick element —</option>
              {Object.entries(elementsByRoute).map(([route, els]) => (
                <optgroup key={route} label={route}>
                  {els.map(el => <option key={el.id} value={el.id}>{el.label}</option>)}
                </optgroup>
              ))}
            </select>
            <input className={s.input} value={dText} onChange={e => setDText(e.target.value)}
              placeholder="Instruction text (English)…"
            />
            <div className={s.builderRow}>
              <select className={s.selectSm} value={dStyle} onChange={e => setDStyle(e.target.value as any)}>
                <option value="redPulse">🔴 Red Pulse</option>
                <option value="gold">✨ Gold Glow</option>
              </select>
              <select className={s.selectSm} value={dInterval} onChange={e => setDInterval(+e.target.value)}>
                <option value={1500}>1.5s</option>
                <option value={2000}>2s</option>
                <option value={3000}>3s</option>
                <option value={4000}>4s</option>
              </select>
              <label className={s.checkLbl}>
                <input type="checkbox" checked={dWait} onChange={e => setDWait(e.target.checked)}/> Wait for click
              </label>
              <button className={s.btnAdd} disabled={!dPickEl} onClick={addDesignerStep} style={{ marginLeft: 'auto' }}>
                + Add step
              </button>
            </div>
          </div>

          {/* Designer steps */}
          {dSteps.length > 0 && <>
            <div className={s.sLabel}>Steps ({dSteps.length})</div>
            <StepList steps={dSteps} onUndo={() => setDSteps(p => p.slice(0, -1))} />
            <div className={s.field}>
              <span className={s.fieldLabel}>Tags</span>
              <input className={s.input} value={dTags} onChange={e => setDTags(e.target.value)}
                placeholder="shipment, create…"
              />
            </div>
            <div className={s.actions}>
              <button className={s.btnGhost} onClick={() => setDSteps([])}>Clear</button>
              <button className={s.btnAmber} disabled={!user} onClick={playDesignerNow}>▶ Play</button>
              <button className={s.btnGreen} disabled={!dTitle.trim()} onClick={saveDesignerFlow}>💾 Save</button>
            </div>
          </>}
        </>}

      </div>

      {/* ══ PINNED SEND BAR (Flows tab) ═══════════════════════════════════════
          Rendered OUTSIDE the scrolling `.body`, so the send action is always
          visible at the bottom of the panel — no more hunting for it below the
          flow grid. Shows the selected flow and a prominent Send button; the
          button is disabled until both a user and a flow are chosen. */}
      {tab === 'flows' && (
        <div className={s.flowFooter}>
          {selectedFlow ? (
            <div className={s.flowFooterInfo}>
              <span className={s.flowFooterIcon}>{selectedFlow.icon}</span>
              <div className={s.flowFooterText}>
                <div className={s.flowFooterTitle}>{selectedFlow.title}</div>
                <div className={s.flowFooterMeta}>
                  {selectedFlow.steps} steps
                  {user?.lang && user.lang !== 'en' ? ` · ${LANG_NAMES[user.lang] ?? user.lang}` : ''}
                </div>
              </div>
            </div>
          ) : (
            <div className={s.flowFooterPlaceholder}>
              {user ? 'Select a flow above to send' : 'Select a user, then a flow'}
            </div>
          )}
          <button
            className={s.btnSend}
            disabled={!selectedFlow || !user}
            onClick={() => {
              if (!selectedFlow || !user) return;
              onSendFlow(user.id, selectedFlow.id);
              addToast({ title: '🧭 Flow Sent', msg: `${selectedFlow.title} → ${user.name}`, icon: '📋', color: 'green' });
              setFlowId(null);
            }}
          >
            🚀 {user ? `Send to ${user.name}` : 'Send flow'}
          </button>
        </div>
      )}
    </div>
  );
};
